import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import * as portalPasswordService from '../services/portalPasswordService';
import { usePortalSiteStore } from '../stores/portalSiteStore';

/**
 * Storefront-branded "forgot your password?" entry point. Posts the
 * username through crm-api → login. The server always returns ok to
 * avoid leaking which usernames exist; we mirror that on the UI by
 * showing a "check your email" confirmation regardless of outcome.
 *
 * Follows the portal's pre-login `loginTheme` (default 'dark') so it
 * matches the welcome/login surfaces. In light mode we force the input
 * white with `!` to beat the global `.dark input { … }` rule in
 * index.css (the storefront document stays in dark mode pre-login).
 */
export function ForgotPasswordPage(): JSX.Element {
	const { t } = useTranslation();
	const [username, setUsername] = useState('');
	const [submitted, setSubmitted] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const loginTheme = usePortalSiteStore((s) => s.site?.loginTheme ?? 'dark');
	const isLight = loginTheme === 'light';

	const pageCls = isLight ? 'bg-surface' : 'bg-brand-page-bg';
	const cardCls = isLight
		? 'bg-surface border-default shadow-xl'
		: 'bg-brand-surface border-brand-border';
	const headingCls = isLight ? 'text-default' : 'text-default';
	const bodyCls = isLight ? 'text-muted' : 'text-brand-muted';
	const labelCls = isLight ? 'text-strong' : 'text-strong';
	const inputCls = isLight
		? '!bg-white border-strong !text-default placeholder-subtle'
		: 'bg-brand-input border-brand-border text-default';

	const handleSubmit = async (
		e: React.FormEvent<HTMLFormElement>,
	): Promise<void> => {
		e.preventDefault();
		if (username.trim().length === 0) return;
		setSubmitting(true);
		try {
			await portalPasswordService.forgotPassword(username.trim());
		} catch {
			// Swallow — login already collapses errors into ok=1 to prevent
			// timing-attack enumeration; we mirror that policy here.
		}
		setSubmitting(false);
		setSubmitted(true);
	};

	return (
		<div
			className={`min-h-dvh flex items-center justify-center px-4 py-8 ${pageCls}`}
		>
			<div
				className={`w-full max-w-md border rounded-xl p-6 space-y-4 ${cardCls}`}
			>
				{submitted ? (
					<>
						<h1 className={`text-2xl font-semibold ${headingCls}`}>
							{t('Check your email')}
						</h1>
						<p className={`text-sm ${bodyCls}`}>
							{t(
								'If an account matches that email or phone number, we just sent a link to reset your password. The link expires in one hour.',
							)}
						</p>
						<Link
							to="/"
							className="inline-block text-sm text-portal-accent hover:underline"
						>
							{t('Back to sign in')}
						</Link>
					</>
				) : (
					<form onSubmit={handleSubmit} className="space-y-4">
						<div>
							<h1 className={`text-2xl font-semibold ${headingCls}`}>
								{t('Reset your password')}
							</h1>
							<p className={`text-sm mt-1 ${bodyCls}`}>
								{t(
									'Enter the email or phone number on your account and we will send a reset link.',
								)}
							</p>
						</div>
						<label className="block">
							<span className={`text-sm font-medium ${labelCls}`}>
								{t('Email or phone')}
							</span>
							<input
								type="text"
								autoComplete="username"
								value={username}
								onChange={(e) => setUsername(e.target.value)}
								className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-portal-accent ${inputCls}`}
								required
							/>
						</label>
						<button
							type="submit"
							disabled={submitting || username.trim().length === 0}
							className="w-full rounded-md bg-portal-accent hover:bg-portal-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2.5 transition-colors"
						>
							{submitting ? t('Sending...') : t('Send reset link')}
						</button>
						<div className="text-sm text-center">
							<Link to="/" className="text-portal-accent hover:underline">
								{t('Back to sign in')}
							</Link>
						</div>
					</form>
				)}
			</div>
		</div>
	);
}
